import moment from 'moment';
import { NO_PRICE } from '../pricelist/quickprice';
import { roundPrice } from '../utils/round';
import { ICancellationRule } from './cancellation';

/** One confirmed booking net-total snapshot (booker & hotel confirmed). */
export interface CancellationWaiverConfirmedTotalPoint {
  date: Date;
  /** Confirmed services net total at this moment. */
  netTotal: number;
}

export interface CalculateCancellationWaiverInput {
  /** Chronological confirmed booking net totals. */
  confirmedTotals: CancellationWaiverConfirmedTotalPoint[];
  /** Event / first service start; cancellation windows are relative to start of this day. */
  eventStart: Date;
  /** Premium (or effective) cancellation rules — widest `daysToEvent` opens the waiver window. */
  rules: ICancellationRule[];
  /** Premium free-deviation rate (typically `0.1`). */
  toleranceRate: number;
  /**
   * Applicable cancellation fees (SM-2791). Used only for Sum.
   * Pass `0` when computing waiver alone; use `NO_PRICE` when fees are unknown.
   */
  cancellationFees: number;
}

export interface CalculateCancellationWaiverResult {
  /** Highest confirmed net total within any cancellation period (0 before a period starts). */
  waiverBasis: number;
  /** When that peak was first reached inside a cancellation period (or period start for carry-in). */
  waiverBasisFirstConfirmedAt: Date | null;
  /** Always ≤ 0: `−(basis × toleranceRate)`. */
  cancellationWaiver: number;
  /** `max(0, cancellationFees + cancellationWaiver)`; `NO_PRICE` if fees are unknown. */
  sum: number;
  priceOnRequest: boolean;
}

function isNoPrice(amount: number): boolean {
  return amount === NO_PRICE;
}

/**
 * Calendar day when the widest cancellation period starts (`eventStart − max(daysToEvent)`).
 *
 * @returns Period start, or `null` when there are no rules.
 */
export function getCancellationWaiverPeriodStart(eventStart: Date, rules: ICancellationRule[]): Date | null {
  if (!rules.length) return null;
  const widestDays = Math.max(...rules.map(r => r.daysToEvent));
  return moment(eventStart).startOf('day').add(-widestDays, 'days').toDate();
}

/**
 * Premium cancellation waiver from peak confirmed booking total inside cancellation windows.
 * Waiver basis = highest confirmed net total on/after the widest period start (ticket example table).
 * Carry-in: if a total was confirmed before the window, it becomes the basis when the window opens.
 *
 * @param input Confirmed totals, rules, tolerance, and fees for Sum.
 * @returns Basis, first-peak date, negative waiver, and floored Sum.
 */
export function calculateCancellationWaiver(input: CalculateCancellationWaiverInput): CalculateCancellationWaiverResult {
  const periodStart = getCancellationWaiverPeriodStart(input.eventStart, input.rules);
  const toleranceRate = input.toleranceRate > 0 ? input.toleranceRate : 0;
  const feesUnknown = isNoPrice(input.cancellationFees);

  const empty: CalculateCancellationWaiverResult = {
    waiverBasis: 0,
    waiverBasisFirstConfirmedAt: null,
    cancellationWaiver: 0,
    sum: feesUnknown ? NO_PRICE : Math.max(0, roundPrice(input.cancellationFees)),
    priceOnRequest: feesUnknown,
  };

  if (!periodStart) {
    return empty;
  }

  const points = [...input.confirmedTotals].sort(
    (a, b) => moment(a.date).valueOf() - moment(b.date).valueOf(),
  );

  let current = 0;
  let currentUnknown = false;
  let peak = 0;
  let peakFirstDate: Date | null = null;
  let enteredPeriod = false;
  let peakUnknown = false;

  const periodStartDay = moment(periodStart).startOf('day');

  for (const point of points) {
    const pointDay = moment(point.date).startOf('day');
    const inPeriod = pointDay.isSameOrAfter(periodStartDay);

    if (inPeriod && !enteredPeriod) {
      enteredPeriod = true;
      // Carry last known total into the window as of period start.
      if (!currentUnknown && current > peak) {
        peak = current;
        peakFirstDate = periodStart;
      } else if (currentUnknown) {
        peakUnknown = true;
      }
    }

    if (isNoPrice(point.netTotal)) {
      currentUnknown = true;
      current = NO_PRICE;
    } else {
      currentUnknown = false;
      current = point.netTotal;
    }

    if (!inPeriod) {
      continue;
    }

    if (currentUnknown) {
      peakUnknown = true;
      continue;
    }

    if (current > peak) {
      peak = current;
      peakFirstDate = point.date;
    }
  }

  if (peakUnknown) {
    return {
      waiverBasis: NO_PRICE,
      waiverBasisFirstConfirmedAt: peakFirstDate,
      cancellationWaiver: NO_PRICE,
      sum: NO_PRICE,
      priceOnRequest: true,
    };
  }

  const waiverBasis = roundPrice(Math.max(0, peak));
  // Credit only — never positive.
  const cancellationWaiver = -roundPrice(waiverBasis * toleranceRate);

  const sum = feesUnknown
    ? NO_PRICE
    : roundPrice(Math.max(0, input.cancellationFees + cancellationWaiver));

  return {
    waiverBasis,
    waiverBasisFirstConfirmedAt: waiverBasis > 0 ? peakFirstDate : null,
    cancellationWaiver,
    sum,
    priceOnRequest: feesUnknown,
  };
}

/** Hotel decision for how much of Sum to invoice (Phase 4). */
export type CancellationWaiveMode = 'WAIVE' | 'PARTIAL' | 'CHARGE';

/**
 * Amount to invoice from Sum + hotel waive choice.
 * Charged amount is clamped to `[0, sum]`.
 */
export function resolveCancellationAmountToInvoice(input: {
  mode: CancellationWaiveMode;
  sum: number;
  chargedAmount?: number | null;
}): number {
  if (isNoPrice(input.sum) || input.sum <= 0) return 0;
  if (input.mode === 'WAIVE') return 0;
  if (input.mode === 'CHARGE') return roundPrice(input.sum);
  const charged = Math.max(0, input.chargedAmount ?? 0);
  return roundPrice(Math.min(charged, input.sum));
}

/**
 * Infer radio mode from a persisted Amount to invoice (stored in `manualCancellationWaiveNet` for this path).
 */
export function inferCancellationWaiveMode(
  amountToInvoice: number | null | undefined,
  sum: number,
): { mode: CancellationWaiveMode; chargedAmount: number | null } {
  if (isNoPrice(sum) || sum <= 0) {
    return { mode: 'CHARGE', chargedAmount: null };
  }
  if (amountToInvoice == null) {
    return { mode: 'CHARGE', chargedAmount: null };
  }
  if (amountToInvoice <= 0) {
    return { mode: 'WAIVE', chargedAmount: null };
  }
  if (Math.abs(amountToInvoice - sum) < 0.005) {
    return { mode: 'CHARGE', chargedAmount: null };
  }
  return { mode: 'PARTIAL', chargedAmount: amountToInvoice };
}
